import { get, set } from 'idb-keyval';

import { GONE_CODES, OPEN_ANSWER_REJECT_CODES, apiErrorCode, recordDropCode } from './apiErrorCode';
import { isOnline, onNetworkChange } from './network';

/**
 * Очередь исходящих действий — чтобы ответы учеников не пропадали без сети.
 *
 * ЧТО БЫЛО. Ответ уходил так:
 *
 *     progressApi.answerBlock(id, payload).catch(() => {});   // ошибка проглочена
 *     setAnswered(...)                                        // но локально «ответил»
 *
 * Отправка не удалась — ошибка выброшена молча, а ученику поставлена галочка
 * «сохранено». Он уверен, что сдал работу; на сервере пусто, куратор ничего не
 * видит. Ровно на это жаловались в Edvibe, и у нас было так же.
 *
 * КАК СТАЛО. Действие сперва ложится в очередь на устройстве и только потом
 * уходит попытка отправки. Подтвердил сервер — запись убирается. Не подтвердил —
 * остаётся и уходит позже: при возврате сети, при следующем запуске приложения
 * или по кнопке в профиле.
 *
 * ПОЧЕМУ IndexedDB, А НЕ localStorage. Очередь должна пережить закрытие
 * приложения и не упереться в пятимегабайтный потолок: письменная работа с
 * вложениями съедает его быстро.
 *
 * ЧЕГО ЗДЕСЬ СОЗНАТЕЛЬНО НЕТ. Очередь не решает, что именно отправлять: она
 * хранит имя действия и его данные, а как их послать — знает `HANDLERS`. Так
 * добавление нового вида действия не трогает саму очередь.
 */

const KEY = 'onebilim:outbox';

/**
 * Кто сейчас за устройством.
 *
 * ⚠️ ЗНАЧЕНИЕ ПЕРЕДАЁТСЯ СНАРУЖИ, А НЕ БЕРЁТСЯ ИЗ ХРАНИЛИЩА ВХОДА НАПРЯМУЮ.
 * Первый вариант читал `useAuthStore` прямо здесь — и владелец не записывался
 * вовсе: очередь и хранилище входа оказывались разными кусками сборки. Ошибка
 * тихая: поле пустое, значит «ничьё», значит отправляем кому угодно. Поймано
 * тестом 05.09.2026, а не в бою.
 *
 * Заодно это правильнее по устройству: хранилищу очереди незачем знать про вход.
 */
let currentOwner: string | undefined;

/**
 * Сообщить очереди, чей сейчас сеанс. Зовётся при входе и выходе.
 *
 * ⚠️ ВХОД ЗАПУСКАЕТ ПРОХОД, ВЫХОД СНИМАЕТ ПОВТОР (26.09.2026, техдолг 299). Без сеанса очередь молчит
 * (`runPass`), поэтому ответы, пролежавшие до входа, уходят именно здесь — когда сеанс появился: после
 * чтения сохранённой сессии при запуске или после входа заново. Повтор, назначенный до выхода, без
 * сеанса ничего не доставит — снимаем его.
 */
export function setOutboxOwner(id: string | undefined): void {
  const prev = currentOwner;
  currentOwner = id;
  if (!id) {
    cancelOutboxRetry();
    return;
  }
  if (id === prev) return;
  // 🔴 ПРОХОД, ИДУЩИЙ В МОМЕНТ ВХОДА, НЕ СЧИТАЕТСЯ (03.10.2026, найдено на стенде). При запуске приложения
  // первый проход стартует ещё без сеанса; сессия дочитывается из хранилища, пока он читает очередь, и
  // `flush()` отсюда возвращал тот самый идущий проход — а он владельца не знал и не отправил ничего.
  // Запись после перезапуска лежала, пока не сменится сеть или ученик не ответит на что-нибудь ещё:
  // 20 секунд без единой попытки, замерено. Теперь после идущего прохода обязательно идёт свой.
  const running = inFlight;
  if (running) void running.then(flush, flush);
  else void flush();
}

export type OutboxItem = {
  /** Стабильный ключ: повторная отправка того же действия не создаёт дубль. */
  id: string;
  kind: string;
  payload: unknown;
  /** Сколько раз пытались — чтобы не долбить сервер бесконечно одним и тем же. */
  attempts: number;
  createdAt: number;

  /**
   * Чей это ответ.
   *
   * 🔴 БЕЗ ЭТОГО ОТВЕТЫ ОДНОГО УЧЕНИКА УХОДИЛИ БЫ ОТ ИМЕНИ ДРУГОГО. Очередь одна
   * на устройство, а устройство общее: школьный компьютер, телефон на двоих
   * сестёр. Ученик ответил без сети и вышел; следующий вошёл — и его сессия
   * отправила чужие ответы СВОИМ пропуском. Работа записалась бы не тому.
   * Найдено аудитом 05.09.2026.
   *
   * У записей, сделанных до появления поля, владельца нет: их отправляем как
   * раньше — потерять готовые ответы хуже.
   */
  ownerId?: string;

  /**
   * Сервер отказал НАВСЕГДА (уроки удалили, ученика отчислили).
   *
   * 🔴 БЕЗ ЭТОГО ОДНА ТАКАЯ ЗАПИСЬ БЛОКИРОВАЛА ВСЮ ОЧЕРЕДЬ. Проход обрывался на
   * первой неудаче, и всё, что стояло за ней, не уходило НИКОГДА: ученик жмёт
   * «синхронизировать», а счётчик не двигается. Теперь такие пропускаем и идём
   * дальше, а через неделю убираем.
   */
  permanentFail?: boolean;

  /**
   * Место вызова ждёт отказ сервера (`enqueue(..., { rejectOnPermanent: true })`).
   * Хранится в записи: отказ может прийти и в позднем проходе, когда связь вернулась.
   */
  rejectOnPermanent?: boolean;

  /**
   * Почему последняя попытка не удалась (промпт 22, решение владельца 18.10.2026): HTTP-код (`0` — запрос не дошёл,
   * сети нет) и код отказа сервера. По ним список «Неотправленные записи» решает, что предложить: временная
   * причина (сеть, 5xx, 408, 429) — «Отправить» и «Удалить»; отказ навсегда — только «Удалить» и причина текстом.
   * Поле только добавляется: у записей прежних версий его нет — там «не удалось» без причины.
   */
  lastStatus?: number;
  lastCode?: string;
};

type Handler = (payload: never) => Promise<unknown>;

const HANDLERS = new Map<string, Handler>();

/** Регистрирует способ отправки для вида действия. Зовётся при старте приложения. */
export function registerOutboxHandler<T>(kind: string, send: (payload: T) => Promise<unknown>): void {
  HANDLERS.set(kind, send as Handler);
}

const changeListeners = new Set<(count: number) => void>();

export function onOutboxChange(l: (count: number) => void): () => void {
  changeListeners.add(l);
  return () => changeListeners.delete(l);
}

const failedListeners = new Set<(count: number) => void>();

/**
 * Сообщает, сколько записей сервер не примет никогда.
 *
 * ⚠️ ЗАЧЕМ (10.09.2026). Такая запись раньше молча жила неделю в счётчике «ждут
 * отправки»: ученик думал, что ответ уйдёт, а сервер уже отказал. Теперь об этом
 * говорится сразу, в полосе состояния связи.
 */
export function onOutboxFailed(l: (count: number) => void): () => void {
  failedListeners.add(l);
  return () => failedListeners.delete(l);
}

const stuckListeners = new Set<(stuck: boolean) => void>();

/**
 * Сообщает, что очередь застряла: сервер отвечает отказом уже несколько попыток
 * подряд, а связь при этом есть.
 *
 * ⚠️ ЗАЧЕМ ОТДЕЛЬНЫЙ СИГНАЛ. Полоска «N ждут отправки» показывает нормальную
 * работу без сети и ученика не тревожит. А вот когда сеть есть, а сервер раз за
 * разом отказывает, молчать нельзя: 07.09.2026 четверо учеников остались с
 * запертым курсом и НИЧЕГО об этом не узнали — приложение рисовало галочки.
 */
export function onOutboxStuck(l: (stuck: boolean) => void): () => void {
  stuckListeners.add(l);
  return () => stuckListeners.delete(l);
}

let stuckNow = false;

function setStuck(v: boolean): void {
  if (stuckNow === v) return;
  stuckNow = v;
  stuckListeners.forEach((l) => l(v));
}

/** Застряла ли очередь прямо сейчас — для первой отрисовки. */
export function isOutboxStuck(): boolean {
  return stuckNow;
}

/**
 * После скольких неудач подряд считаем, что дело не в мигнувшей связи.
 * Три попытки — это около минуты по задержкам ниже.
 */
export const OUTBOX_STUCK_AFTER_ATTEMPTS = 3;

/**
 * Задержки между повторами, по номеру попытки.
 *
 * ⚠️ БЕЗ ЭТОГО ПОВТОРА НЕ БЫЛО ВООБЩЕ. Проход обрывался на первой временной
 * неудаче и возобновлялся только при запуске приложения, возврате сети или по
 * кнопке в профиле. Ученик, у которого отметка раздела упала в 500, мог сидеть
 * в уроке сколько угодно — повтор не наступал никогда.
 *
 * Растут, чтобы не долбить сервер, который и так отказывает; предел — пять
 * минут, дальше повторяем с ним же.
 */
export const OUTBOX_RETRY_DELAYS_MS = [5_000, 15_000, 45_000, 120_000, 300_000];

export function retryDelayFor(attempts: number): number {
  const i = Math.min(Math.max(attempts, 1), OUTBOX_RETRY_DELAYS_MS.length) - 1;
  return OUTBOX_RETRY_DELAYS_MS[i];
}

let retryTimer: ReturnType<typeof setTimeout> | undefined;

function scheduleRetry(attempts: number): void {
  if (retryTimer !== undefined) return; // повтор уже назначен
  retryTimer = setTimeout(() => {
    retryTimer = undefined;
    void flush();
  }, retryDelayFor(attempts));
}

/** Снимает назначенный повтор. Нужен при выходе и в проверках. */
export function cancelOutboxRetry(): void {
  if (retryTimer !== undefined) {
    clearTimeout(retryTimer);
    retryTimer = undefined;
  }
}

/**
 * Очередь в памяти — на то время, пока хранилище отказывает.
 *
 * 🔴 ДО 02.10.2026 ЕЁ НЕ БЫЛО, ХОТЯ КОММЕНТАРИЙ ОБЕЩАЛ. `write()` глотал отказ и писал «очередь живёт только в
 * этой сессии», а `read()` снова шёл в хранилище и получал пустоту: запись ученика исчезала, а блок говорил
 * «сохранена на устройстве» (`docs/CHECK-2026-10-01-OFFLINE-AUDIO.md` §1.3, дыра 1). Теперь отказавшая запись
 * остаётся здесь и уходит обычными проходами, пока страница открыта; место вызова при этом узнаёт об отказе
 * (`OutboxStorageError`) и не обещает ученику того, чего нет.
 *
 * Хранилище ожило — следующая же запись кладёт туда всё накопленное, и память отпускается.
 */
let volatile: OutboxItem[] | null = null;

async function read(): Promise<OutboxItem[]> {
  if (volatile) return volatile;
  try {
    return (await get<OutboxItem[]>(KEY)) ?? [];
  } catch {
    return [];
  }
}

/** Возвращает, легла ли очередь в хранилище устройства. `false` — она только в памяти этой страницы. */
async function write(items: OutboxItem[]): Promise<boolean> {
  let stored = true;
  try {
    await set(KEY, items);
    volatile = null;
  } catch {
    stored = false;
    volatile = items;
  }
  // ⚠️ Отказанное навсегда в «ждут отправки» не считается: ждать там нечего.
  // Оно показывается отдельно и сразу (`onOutboxFailed`), а не молчит неделю.
  const failed = items.filter((i) => i.permanentFail).length;
  changeListeners.forEach((l) => l(items.length - failed));
  failedListeners.forEach((l) => l(failed));
  return stored;
}

/**
 * Запись не легла в хранилище устройства и на сервер сейчас не ушла.
 *
 * Она ещё жива в памяти страницы и уйдёт, если страницу не закроют, — но «сохранена на устройстве» про неё
 * сказать нельзя. Бросается только тем, кто попросил (`requireStored`): запись голоса.
 */
export class OutboxStorageError extends Error {
  constructor() {
    super('outbox storage failed');
    this.name = 'OutboxStorageError';
  }
}

export function isOutboxStorageError(err: unknown): err is OutboxStorageError {
  return err instanceof OutboxStorageError || (err as { name?: string } | null)?.name === 'OutboxStorageError';
}

/** Чем кончилась жизнь записи в очереди. */
export type OutboxSettle =
  /** сервер принял */
  | 'delivered'
  /** сервер отказал так, что повторять бессмысленно, и запись снята */
  | 'rejected'
  /** стёр ученик */
  | 'removed'
  /** вышел срок */
  | 'expired';

const settledListeners = new Set<(item: OutboxItem, how: OutboxSettle, err?: unknown) => void>();

/**
 * Запись ушла из очереди — и почему. Нужен тем, у кого за записью что-то лежит отдельно: файл записи на
 * телефоне, заметка ученику об отказе, известие серверу о стёртом.
 */
export function onOutboxSettled(l: (item: OutboxItem, how: OutboxSettle, err?: unknown) => void): () => void {
  settledListeners.add(l);
  return () => settledListeners.delete(l);
}

function settled(item: OutboxItem, how: OutboxSettle, err?: unknown): void {
  settledListeners.forEach((l) => {
    try {
      l(item, how, err);
    } catch {
      /* слушатель не должен ронять проход очереди */
    }
  });
}

/** Цепочка изменений хранилища: каждое следующее ждёт окончания предыдущего. */
let storageChain: Promise<void> = Promise.resolve();

/**
 * Прочитать — изменить — записать, строго по очереди с другими изменениями.
 *
 * ⚠️ ПРОХОД ОТПРАВКИ БОЛЬШЕ НЕ ПИШЕТ СВОЙ СНИМОК ЦЕЛИКОМ. Раньше он читал очередь
 * в начале и после каждой отправки записывал этот снимок — ответ, положенный в
 * очередь во время прохода, затирался (найдено 10.09.2026 при разборе отказов).
 *
 * ⚠️ И ПЕРЕЧИТЫВАТЬ МАЛО — НУЖНА ЦЕПОЧКА. Два изменения, идущие одновременно
 * (фоновый проход убирает доставленное, `enqueue` кладёт новый ответ), читали одно
 * и то же, и последнее записанное стирало второе. Проверка
 * `outboxReject.test.ts` поймала это на пропавшем ответе.
 */
function update(fn: (items: OutboxItem[]) => OutboxItem[]): Promise<boolean> {
  const run = storageChain.then(async () => write(fn(await read())));
  storageChain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/** Та же запись, а не новая с тем же ключом, положенная позже. */
const sameItem = (a: OutboxItem, b: OutboxItem) => a.id === b.id && a.createdAt === b.createdAt;

/** Моя ли это запись: чужую с общего устройства не показываем и не трогаем. */
const mine = (i: OutboxItem) => !i.ownerId || !currentOwner || i.ownerId === currentOwner;

/**
 * Запись очереди по её ключу — чтобы задание после перезапуска знало, что его ответ ещё лежит на устройстве.
 *
 * 🔴 ЗАЧЕМ (02.10.2026, дыра 4 сверки). «В очереди» было состоянием экрана: ученик возвращался в урок,
 * ответа с сервера ещё нет — блок пустой, «Записать». Он решал, что запись пропала, и записывал заново.
 */
export async function queuedItem(id: string): Promise<OutboxItem | undefined> {
  return (await read()).find((i) => i.id === id && mine(i));
}

/** Все мои записи одного вида — для отметок «ждёт отправки» при открытии урока. */
export async function queuedOfKind(kind: string): Promise<OutboxItem[]> {
  return (await read()).filter((i) => i.kind === kind && mine(i));
}

/** Ученик сам стёр запись из очереди. Возвращает стёртое, если оно было. */
export async function removeQueued(id: string): Promise<OutboxItem | undefined> {
  let gone: OutboxItem | undefined;
  await update((items) => {
    gone = items.find((i) => i.id === id && mine(i));
    return gone ? items.filter((i) => i !== gone) : items;
  });
  if (gone) settled(gone, 'removed');
  return gone;
}

/** Сколько действий ждёт отправки — для отметки в профиле. Отказанное навсегда не ждёт. */
export async function pendingCount(): Promise<number> {
  return (await read()).filter((i) => !i.permanentFail).length;
}

/** Сколько действий сервер не принял навсегда — ученику показываем сразу. */
export async function failedCount(): Promise<number> {
  return (await read()).filter((i) => i.permanentFail).length;
}

/**
 * Ставит действие в очередь и сразу пробует отправить.
 *
 * Возвращает `true`, если сервер подтвердил приём прямо сейчас. Место вызова по
 * этому значению решает, показывать галочку «сохранено» или отметку «ждёт
 * отправки» — обманывать ученика мы больше не будем.
 *
 * ⚠️ `rejectOnPermanent` (10.09.2026, договор `empty-open-answers.md`). Без флага
 * постоянный отказ сервера молча превращался в «ждёт отправки» на неделю. С флагом
 * `enqueue` бросает ИСХОДНУЮ ошибку обработчика — место вызова показывает её у
 * задания. Отказ «пустой ответ» к тому же убирает запись из очереди: она не
 * пройдёт, пока ученик не ответит заново.
 */
export async function enqueue(
  kind: string,
  id: string,
  payload: unknown,
  opts: { rejectOnPermanent?: boolean; requireStored?: boolean } = {},
): Promise<boolean> {
  outcomes.delete(id);
  waiting.add(id);
  try {
    // Один и тот же ответ, отправленный дважды, заменяет сам себя: ученик мог
    // передумать и ответить заново, пока первая попытка ещё лежала в очереди.
    let replaced: OutboxItem | undefined;
    const stored = await update((items) => {
      replaced = items.find((i) => i.id === id);
      return [
        ...items.filter((i) => i.id !== id),
        {
          id,
          kind,
          payload,
          attempts: 0,
          createdAt: Date.now(),
          ownerId: currentOwner,
          ...(opts.rejectOnPermanent ? { rejectOnPermanent: true } : {}),
        },
      ];
    });
    // Прежняя запись с тем же ключом вытеснена новой — за ней мог остаться файл на телефоне.
    if (replaced) settled(replaced, 'removed');

    // 🔴 «НЕ ДОШЛО» ПРИ ОТКАЗАВШЕМ ХРАНИЛИЩЕ — ЭТО НЕ «ЖДЁТ ОТПРАВКИ» (02.10.2026). Запись только в памяти
    // страницы; тому, кто попросил (`requireStored`), говорим об этом ошибкой, а не возвращаем `false`,
    // которое место вызова читает как «сохранена на устройстве».
    const unstored = () => {
      if (!stored && opts.requireStored) throw new OutboxStorageError();
      return false;
    };

    if (!isOnline()) return unstored();
    // ⚠️ Проход мог уже идти (старт приложения, повтор, возврат сети) со снимком,
    // прочитанным до нашей записи. Дожидаемся его и, если он нашу запись не
    // разобрал, запускаем свой — иначе отказ сервера до места вызова не дойдёт.
    while (inFlight) await inFlight;
    if (!outcomes.has(id)) await flush();

    const outcome = outcomes.get(id);
    if (outcome && 'error' in outcome) {
      if (outcome.reject) throw outcome.error;
      return unstored();
    }
    return outcome !== undefined ? true : unstored();
  } finally {
    waiting.delete(id);
    outcomes.delete(id);
  }
}

/** Идущий проход отправки — один на всё приложение. */
let inFlight: Promise<string[]> | null = null;

type Outcome = { delivered: true } | { error: unknown; reject: boolean };

/** Исходы записей, которых прямо сейчас ждёт `enqueue`. */
const outcomes = new Map<string, Outcome>();
const waiting = new Set<string>();

/**
 * Пробует отправить всё, что накопилось. Возвращает идентификаторы дошедшего.
 *
 * Порядок сохраняется: ответы уходят в том же порядке, в каком ученик их давал.
 * Временная неудача откладывает до следующего прохода свой вид и то, что его ждёт
 * (`WAITS_FOR`), остальные виды идут дальше (30.10.2026, до этого проход обрывался).
 */
/** Сколько храним неотправленное, если оно так и не ушло. Неделя — решение заказчика. */
export const OUTBOX_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Виды записей, которые по сроку НЕ стираются (решение владельца 02.10.2026).
 *
 * 🔴 ЗАПИСЬ ГОЛОСА НЕ СТИРАЕМ МОЛЧА. Текстовый ответ ученик наберёт заново; три минуты речи — нет. До этой
 * правки запись, пролежавшая неделю без связи, исчезала с одной строкой в консоли, и ученик не узнавал об
 * этом никак. Теперь она остаётся, у задания написано «не отправлена больше недели», и стереть её может
 * только сам ученик кнопкой. По виду, а не по флагу в записи: под правило должны попасть и записи, уже
 * лежащие в очереди с прошлой версии приложения.
 */
const NO_TTL_KINDS: ReadonlySet<string> = new Set(['lesson-audio']);

/**
 * Чего вид ждёт, кроме себя самого, когда в проходе что-то споткнулось (30.10.2026, T5).
 *
 * 🔴 ОТМЕТКА РАЗДЕЛА НЕ ОБГОНЯЕТ ОТВЕТЫ УРОКА. Ушла бы раньше неотправленного ответа — сервер ответил бы
 * `409 open_answers_missing`, очередь сняла бы отметку как отказ, а плеер снял бы с задания «отвечено»: раздел
 * не закрылся бы и после того, как ответ дойдёт. Пока ответ ждёт, ждёт и отметка — как при сплошном обрыве
 * прохода, который был до этой правки.
 */
const WAITS_FOR: Readonly<Record<string, readonly string[]>> = {
  'section-complete': ['lesson-answer', 'lesson-audio'],
};

/** Запись лежит дольше срока. У голоса это повод сказать ученику, у остального — стереть. */
export function isOutboxItemStale(item: Pick<OutboxItem, 'createdAt'>, now = Date.now()): boolean {
  return now - item.createdAt >= OUTBOX_TTL_MS;
}

/**
 * Отказ навсегда или временный.
 *
 * ⚠️ РАЗЛИЧАТЬ ОБЯЗАТЕЛЬНО. Нет сети, сервер перезапускается, шлюз ответил 502 —
 * это «попробуем позже», и прекращать проход правильно. А вот 400 «урока больше
 * нет» или 403 «вы отчислены» не пройдут никогда: упираясь в такую запись, мы
 * навсегда хороним всё, что стоит за ней в очереди.
 *
 * 401 и 429 к постоянным НЕ относим: первое лечится обновлением пропуска,
 * второе — ожиданием.
 */
function statusOf(err: unknown): number | undefined {
  return (err as { response?: { status?: number }; status?: number })?.response?.status
    ?? (err as { status?: number })?.status;
}

function isPermanent(err: unknown): boolean {
  const status = statusOf(err);
  if (typeof status !== 'number') return false; // сети нет — временное
  return status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429;
}

export function flush(): Promise<string[]> {
  if (!isOnline()) return Promise.resolve([]);
  // Проход уже идёт — второй параллельный удвоил бы запросы; ждущий получит его итог.
  if (inFlight) return inFlight;
  inFlight = runPass().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * Отправить одну запись — «Отправить сейчас» и «Сдать» (промпт 22, 18.10.2026). Возвращает дошедшее, как `flush`.
 *
 * ⚠️ ТОТ ЖЕ ЗАМОК ПРОХОДА. Идёт общий проход — сперва дожидаемся его: он мог уже отправить эту запись, а второй
 * параллельный проход отправил бы её дважды. Свой проход — только по этой записи, остальная очередь ждёт своей
 * очереди: ученик нажал на одну строку, а не «Отправить все». Просроченное чистится, как в общем проходе.
 */
export async function flushOne(id: string): Promise<string[]> {
  if (!isOnline()) return [];
  while (inFlight) {
    const done = await inFlight.catch(() => [] as string[]);
    if (done.includes(id)) return [id];
  }
  const target = await queuedItem(id);
  if (!target) return [];
  // Ученик сам нажал «Отправить» у записи с отказом «навсегда» (403 бывает временным, прочие 4xx — неизвестно;
  // решение владельца 18.10): даём ещё одну попытку. Общий проход такие по-прежнему пропускает — повтор только руками.
  if (target.permanentFail) {
    await update((cur) => cur.map((i) => (i.id === id && mine(i) ? { ...i, permanentFail: false } : i)));
  }
  inFlight = runPass(id).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function runPass(only?: string): Promise<string[]> {
  const delivered: string[] = [];
  const record = (id: string, outcome: Outcome) => {
    if (waiting.has(id)) outcomes.set(id, outcome);
  };
  /** Та же запись, а не новая с тем же ключом, положенная во время отправки. */
  const same = sameItem;

  let items = await read();

  // ⚠️ Просроченное убираем ДО отправки: неделя — предел, о котором
  // договорились. Дольше держать смысла нет, урок давно закрыт.
  // Запись голоса под срок не попадает (`NO_TTL_KINDS`).
  const isFresh = (i: OutboxItem) => NO_TTL_KINDS.has(i.kind) || !isOutboxItemStale(i);
  const stale = items.filter((i) => !isFresh(i));
  if (stale.length > 0) {
    console.warn('[очередь] убрано просроченного:', stale.length);
    items = items.filter(isFresh);
    await update((cur) => cur.filter(isFresh));
    stale.forEach((i) => settled(i, 'expired'));
  }

  // 🔴 БЕЗ СЕАНСА НЕ ШЛЁМ НИЧЕГО (26.09.2026, техдолг 299). Запрос без ключа всё равно получит 401, а 401
  // выводит на вход и ПЕРЕЗАГРУЖАЕТ страницу (`api/client.ts`, logoutAndReload) — после чего очередь при
  // старте шлёт тот же ответ снова. Так на проде две вкладки на экране входа слали ответ раз в секунду,
  // 8 741 и 5 637 раз, а ученик не успевал ввести пароль. Раньше пустой владелец здесь значил «не знаем
  // чей — шлём», и проверка чужого ниже его пропускала. Ответы ждут входа: `setOutboxOwner` запустит проход.
  //
  // ⚠️ Владельца спрашиваем ЗДЕСЬ, после чтения очереди, а не в начале прохода (03.10.2026): чтение
  // хранилища не мгновенное, и сеанс за это время мог появиться.
  const me = currentOwner;
  if (!me) return delivered;

  // Проход одной записи (`flushOne`) — после чистки просроченного, чтобы чистка шла всегда одинаково.
  if (only !== undefined) items = items.filter((i) => i.id === only);

  // 🔴 ВРЕМЕННАЯ НЕУДАЧА ДЕРЖИТ ТОЛЬКО СВОЙ ВИД (30.10.2026, T5). Прежде проход обрывался на первой же неудаче,
  // и запись голоса, которая на медленной связи не укладывается в предел загрузки, держала за собой всё: слова,
  // спринт, тренажёры. Теперь споткнувшийся вид до конца прохода в стороне (порядок внутри вида сохраняется),
  // остальные идут. Отметка раздела ждёт ещё и ответы урока (`WAITS_FOR`). Повтор и сигнал — после прохода.
  const failedKinds = new Set<string>();
  const held = (kind: string) => failedKinds.has(kind) || (WAITS_FOR[kind] ?? []).some((k) => failedKinds.has(k));
  let retryAttempts = 0;
  let stuck = false;

  for (const item of items) {
    // ⚠️ ЧУЖОЕ НЕ ТРОГАЕМ. Отправить ответ ученика А пропуском ученика Б
    // значит записать его работу не тому. Запись остаётся и уйдёт, когда
    // хозяин войдёт снова.
    if (item.ownerId && me && item.ownerId !== me) continue;
    if (item.permanentFail) continue; // уже знаем, что не пройдёт
    if (held(item.kind)) continue; // свой вид или то, чего он ждёт, уже споткнулось в этом проходе
    const send = HANDLERS.get(item.kind);
    if (!send) continue; // неизвестный вид — оставляем, вдруг обработчик появится
    try {
      await send(item.payload as never);
      delivered.push(item.id);
      record(item.id, { delivered: true });
      await update((cur) => cur.filter((i) => !same(i, item)));
      settled(item, 'delivered');
    } catch (err) {
      if (isPermanent(err)) {
        // ⚠️ НЕ ОБРЫВАЕМ ПРОХОД: помечаем и идём дальше. Иначе одна такая
        // запись хоронит всё, что стоит за ней, и кнопка «синхронизировать»
        // перестаёт что-либо решать.
        //
        // Пустой открытый ответ не пройдёт, пока ученик его не переделает, —
        // такую запись убираем, а не держим неделю (`empty-open-answers.md`).
        //
        // ⚠️ ОПОЗДАВШАЯ ЗАПИСЬ ГОЛОСА — ТОЖЕ СНИМАЕТСЯ (02.10.2026, договор `audio-upload.md` §3.1). Сервер
        // ответил 409: работу уже приняли или ученик сдал её позже с другого устройства. Повтор получит тот
        // же отказ, а запись на неделю осела бы в «не отправлено». Ученику об этом говорит слушатель
        // `onOutboxSettled` — отказ может прийти в фоновом проходе, когда задания на экране нет.
        const code = apiErrorCode(err) ?? '';
        // Удалённый раздел (`section_not_found`) — тоже: засчитывать больше нечего (техдолг 265).
        const drop =
          !!item.rejectOnPermanent && (OPEN_ANSWER_REJECT_CODES.has(code) || recordDropCode(code) || GONE_CODES.has(code));
        record(item.id, { error: err, reject: !!item.rejectOnPermanent });
        await update((cur) =>
          drop
            ? cur.filter((i) => !same(i, item))
            : cur.map((i) =>
                same(i, item)
                  ? { ...i, permanentFail: true, attempts: i.attempts + 1, lastStatus: statusOf(err) ?? 0, lastCode: code || undefined }
                  : i,
              ),
        );
        if (drop) settled(item, 'rejected', err);
        continue;
      }
      // ⚠️ 401, а сеанса уже нет: перехватчик сделал одну попытку продления, она не удалась, и ученика
      // вывели на вход. Повторять нечем — ключа нет. Ни попытки, ни таймера: запись ждёт входа того же
      // ученика (`setOutboxOwner`). Если же сеанс жив (продление не дошло из-за сети), это обычная
      // временная неудача ниже — с паузами от 5 секунд.
      if (statusOf(err) === 401 && !currentOwner) break;
      // Временная неудача: помечаем попытку, вид — до конца прохода в сторону (см. выше). Повтор проход
      // назначает себе обязательно, а не ждёт перезапуска приложения; пауза — по самому свежему из
      // споткнувшихся, чтобы ответ с одной неудачей не ждал пяти минут из-за давно застрявшей записи.
      const attempts = item.attempts + 1;
      const lastStatus = statusOf(err) ?? 0;
      const lastCode = apiErrorCode(err) || undefined;
      await update((cur) => cur.map((i) => (same(i, item) ? { ...i, attempts, lastStatus, lastCode } : i)));
      failedKinds.add(item.kind);
      retryAttempts = retryAttempts === 0 ? attempts : Math.min(retryAttempts, attempts);
      if (attempts >= OUTBOX_STUCK_AFTER_ATTEMPTS && isOnline()) stuck = true;
    }
  }
  // Сигнал — по итогу прохода, а не по каждой записи: иначе дошедшее слово гасило бы его, а застрявшая следом
  // запись зажигала снова, и плашка мигала бы на каждом проходе. Нет ни застрявшего, ни дошедшего — сигнал
  // остаётся прежним, как и раньше.
  if (stuck) setStuck(true);
  else if (delivered.length > 0) setStuck(false);
  if (retryAttempts > 0) scheduleRetry(retryAttempts);
  return delivered;
}

/**
 * Включает автоматические попытки: при старте приложения и при каждом появлении
 * сети. Возвращает функцию отписки.
 */
export function initOutbox(): () => void {
  void flush();
  const off = onNetworkChange((online) => {
    if (online) void flush();
  });
  // 🔴 ВЕРНУЛИСЬ НА ЭКРАН — ПРОХОД СРАЗУ (02.10.2026, дыра 6 сверки). Очередь слушала только запуск и смену
  // сети: ученик свернул приложение посреди отправки, запрос оборвался, вернулся — а повтор стоит на пяти
  // минутах. Сеть при этом не менялась, и повода для прохода не было. Проход один на приложение (`flush`),
  // так что частые возвраты запросов не множат.
  const onVisible = () => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') void flush();
  };
  const canListen = typeof document !== 'undefined' && typeof window !== 'undefined';
  if (canListen) {
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
  }
  return () => {
    cancelOutboxRetry();
    off();
    if (canListen) {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    }
  };
}
